import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  RefreshCw,
  Settings,
  Plug,
  MoreVertical,
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Trash2,
} from 'lucide-react';
import type { IntegrationConfig } from '../types/integration.types';
import { useIntegrationStore } from '../store/integration-store';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import {
  SyncAbortedError,
  SyncWaitTimeoutError,
  summarizeSyncJob,
  type HubspotSyncJobStatus,
} from '@/lib/hubspot-sync';
import { HubspotSyncResultList } from './hubspot-sync-result-list';

interface Props {
  config: IntegrationConfig;
  onEdit: (config: IntegrationConfig) => void;
  onViewLogs: (config: IntegrationConfig) => void;
}

const TYPE_LABELS: Record<string, string> = {
  SAP_S4HANA: 'SAP S/4HANA',
  SAP_C4C: 'SAP C4C (CRM)',
  HUBSPOT: 'HubSpot CRM',
};

function activeSyncs(config: IntegrationConfig): string[] {
  if (config.type === 'SAP_C4C') return ['Oportunidades'];
  const extra = (config.extraConfig ?? {}) as Record<string, unknown>;

  if (config.type === 'HUBSPOT') {
    const syncs: string[] = [];
    if (extra.syncCompanies !== false) syncs.push('Empresas');
    if (extra.syncContacts !== false) syncs.push('Contactos');
    if (extra.syncDeals !== false) syncs.push('Oportunidades');
    return syncs;
  }

  const syncs = ['Materiales'];
  if (extra.syncProfitCenters === true) syncs.push('Profit Centers');
  if (extra.syncLobs === true) syncs.push('LOB / Segment');
  if (extra.syncCountries === true) syncs.push('Países');
  return syncs;
}

const STATUS_CONFIG: Record<string, { icon: any; color: string; label: string }> = {
  SUCCESS: { icon: CheckCircle2, color: 'text-green-500', label: 'Exitoso' },
  PARTIAL: { icon: AlertTriangle, color: 'text-yellow-500', label: 'Parcial' },
  FAILED: { icon: XCircle, color: 'text-red-500', label: 'Fallido' },
  RUNNING: { icon: RefreshCw, color: 'text-blue-500', label: 'En ejecución' },
};

// Estima la próxima corrida sumando el intervalo al último sync.
// El worker tickea cada 5 min, así que la corrida real puede llegar hasta 5 min después.
function formatNextSync(config: IntegrationConfig): string {
  if (!config.isActive) return 'Pausada — activá la integración para programar';
  if (!config.lastSyncAt) return 'Pendiente — primera corrida en los próximos 5 min';

  const next = new Date(
    new Date(config.lastSyncAt).getTime() + config.syncIntervalMinutes * 60_000
  );
  const diffMs = next.getTime() - Date.now();
  const absMin = Math.round(Math.abs(diffMs) / 60_000);

  if (diffMs <= 0) {
    return `Lista para correr (vencida hace ${absMin} min) — el worker la dispara dentro de 5 min`;
  }
  if (absMin < 60) return `${next.toLocaleString('es')} — en ${absMin} min`;
  const hours = Math.floor(absMin / 60);
  const mins = absMin % 60;
  return `${next.toLocaleString('es')} — en ${hours}h ${mins}m`;
}

/** El 503 de la cola trae un texto pensado para la persona; `mensajeDeError` lo descarta por ser 5xx. */
function textoDel503(err: unknown): string | null {
  const r = (err as { response?: { status?: number; data?: { error?: unknown } } })?.response;
  return r?.status === 503 && typeof r.data?.error === 'string' && r.data.error.trim() !== ''
    ? r.data.error
    : null;
}

export function IntegrationConfigCard({ config, onEdit, onViewLogs }: Props) {
  const { testConnection, triggerSync, triggerHubspotSync, deleteConfig, syncing } =
    useIntegrationStore();
  const { toast } = useToast();
  const [testing, setTesting] = useState(false);
  const [hubspotResult, setHubspotResult] = useState<HubspotSyncJobStatus | null>(null);
  const isSyncing = syncing === config.id;
  const syncAbort = useRef<AbortController | null>(null);
  // Al cerrar la pantalla el sondeo se corta: no sigue consultando ni avisa a nadie.
  useEffect(() => () => syncAbort.current?.abort(), []);

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await testConnection(config.id);
      if (result.success) {
        toast({ title: result.message, variant: 'success' });
      } else {
        toast({ title: result.message, variant: 'destructive' });
      }
    } catch (error: any) {
      toast({
        title: 'Error al probar conexion',
        description: error?.response?.data?.error || error.message,
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  // HubSpot: el «Importar» se encola; se espera al worker y se muestra qué
  // entidad anduvo y cuál no (antes, si contactos fallaba, todo daba error).
  const handleHubspotImport = async () => {
    syncAbort.current?.abort();
    const controller = new AbortController();
    syncAbort.current = controller;
    try {
      const final = await triggerHubspotSync(config.id, {
        signal: controller.signal,
        onAlreadyRunning: () =>
          toast({
            title: 'Ya había una importación en curso; mostrando su resultado',
            variant: 'default',
          }),
      });
      setHubspotResult(final);
      const summary = summarizeSyncJob(final);
      toast({ ...summary, duration: 10000 });
    } catch (error: unknown) {
      if (error instanceof SyncAbortedError || controller.signal.aborted) return;
      toast({
        title: 'No se pudo sincronizar',
        description:
          error instanceof SyncWaitTimeoutError
            ? error.message
            : (textoDel503(error) ?? mensajeDeError(error, 'Probá de nuevo en unos minutos.')),
        variant: 'destructive',
      });
    }
  };

  const handleSync = async (direction: 'inbound' | 'outbound') => {
    if (config.type === 'HUBSPOT') {
      await handleHubspotImport();
      return;
    }
    try {
      const result = await triggerSync(config.id, direction);
      toast({
        title: 'Sincronización completada',
        description: `${result.successCount} exitosos, ${result.failedCount} fallidos, ${result.skippedCount} omitidos`,
        variant: 'success',
      });
    } catch (error: any) {
      const details = error?.response?.data?.details;
      toast({
        title: details?.status
          ? `Error al sincronizar (HTTP ${details.status})`
          : 'Error al sincronizar',
        description: details
          ? [
              details.hint,
              details.url && `URL: ${details.url}`,
              details.response && `Respondio: "${details.response}"`,
            ]
              .filter(Boolean)
              .join('\n')
          : error?.response?.data?.error || error.message,
        variant: 'destructive',
        duration: 15000,
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteConfig(config.id);
      toast({ title: 'Integración desactivada', variant: 'success' });
    } catch (error: any) {
      toast({
        title: 'Error al desactivar',
        description: error?.response?.data?.error || error.message,
        variant: 'destructive',
      });
    }
  };

  const statusConfig = config.lastSyncStatus ? STATUS_CONFIG[config.lastSyncStatus] : null;
  const StatusIcon = statusConfig?.icon;

  return (
    <Card className={!config.isActive ? 'opacity-60' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Plug className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">{config.name}</CardTitle>
              <p className="text-sm text-muted-foreground">{TYPE_LABELS[config.type]}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={config.isActive ? 'default' : 'secondary'}>
              {config.isActive ? 'Activa' : 'Inactiva'}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(config)}>
                  <Settings className="h-4 w-4 mr-2" />
                  Editar
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onViewLogs(config)}>
                  <Clock className="h-4 w-4 mr-2" />
                  Ver logs
                </DropdownMenuItem>
                {config.isActive && (
                  <DropdownMenuItem onClick={handleDelete} className="text-red-500">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Desactivar
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* URL & Auth */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">URL Base</p>
              <p className="font-mono text-xs truncate">{config.baseUrl}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Autenticación</p>
              <p>{config.authType}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Intervalo de sync</p>
              <p>{config.syncIntervalMinutes} minutos</p>
            </div>
            <div>
              <p className="text-muted-foreground">Último sync</p>
              <div className="flex items-center gap-1">
                {StatusIcon && <StatusIcon className={`h-3.5 w-3.5 ${statusConfig!.color}`} />}
                <p>
                  {config.lastSyncAt ? new Date(config.lastSyncAt).toLocaleString('es') : 'Nunca'}
                </p>
              </div>
            </div>
            <div className="col-span-2">
              <p className="text-muted-foreground">Proximo sync</p>
              <p className="text-xs">{formatNextSync(config)}</p>
            </div>
          </div>

          {/* Sub-syncs activos */}
          <div className="space-y-1.5">
            <p className="text-sm text-muted-foreground">Sincronizando</p>
            <div className="flex flex-wrap gap-1.5">
              {activeSyncs(config).map((s) => (
                <Badge key={s} variant="outline" className="text-xs">
                  {s}
                </Badge>
              ))}
            </div>
          </div>

          {/* Actions */}
          {config.isActive && (
            <div className="flex gap-2 pt-2 border-t">
              <Button
                size="sm"
                variant="outline"
                onClick={handleTest}
                disabled={testing || isSyncing}
              >
                <Plug className="h-4 w-4 mr-1" />
                {testing ? 'Probando...' : 'Probar'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleSync('inbound')}
                disabled={isSyncing}
              >
                <ArrowDownToLine className="h-4 w-4 mr-1" />
                {isSyncing ? 'Sincronizando...' : 'Importar'}
              </Button>
              {config.type !== 'HUBSPOT' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSync('outbound')}
                  disabled={isSyncing}
                >
                  <ArrowUpFromLine className="h-4 w-4 mr-1" />
                  Exportar
                </Button>
              )}
            </div>
          )}
          {config.isActive && config.type === 'HUBSPOT' && hubspotResult && (
            <HubspotSyncResultList status={hubspotResult} />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
